import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-data-table.css?inline';
import { withCsrf } from '../../lib/csrf.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PkColumnDef {
    key: string;
    label: string;
    /** 'text' | 'date' | 'chips' | 'duration' | 'bool-tick' */
    type?: string;
}

/**
 * Per-value chip style overrides. The map key is matched
 * case-insensitively against the chip's label. Values map to the same
 * shape used by the built-in CHIP_COLOURS palette.
 */
export interface PkChipStyle {
    bg: string;
    fg: string;
    border?: string;
}

export interface PkDataTableConfig {
    src: string;
    pageSize?: number;
    columns: PkColumnDef[];
    /**
     * Empty-state copy shown when an active filter narrowed the result set
     * to zero. The consumer toggles the `filter-active` attribute on the
     * element to switch from the default "No rows yet." copy to this one.
     */
    emptyFilterText?: string;
    /**
     * Per-value chip style overrides. Use to map domain status values
     * (e.g. `"Active"`, `"Suspended"`) to a custom palette without
     * touching the built-in CHIP_COLOURS table.
     */
    chipStyles?: Record<string, PkChipStyle>;
}

type LoadState = 'idle' | 'loading' | 'ok' | 'error';

// ---------------------------------------------------------------------------
// Helpers (ported from al-table; no direct copy — adapted for the kit)
// ---------------------------------------------------------------------------

const CHIP_COLOURS: Record<string, { bg: string; fg: string; border: string }> = {
    error:     { bg: '#fdecec', fg: '#8b1a1a', border: '#f4c4c4' },
    warn:      { bg: '#fff8ec', fg: '#7a4a0d', border: '#f9d88f' },
    warning:   { bg: '#fff8ec', fg: '#7a4a0d', border: '#f9d88f' },
    info:      { bg: '#edf4ff', fg: '#1a4e8c', border: '#c2d9f8' },
    debug:     { bg: '#f5f5f5', fg: '#555',    border: '#ddd'    },
    open:      { bg: '#eaf7f6', fg: '#0c5c58', border: '#b4e0dc' },
    completed: { bg: '#eaf7f6', fg: '#0c5c58', border: '#b4e0dc' },
    aborted:   { bg: '#fdecec', fg: '#8b1a1a', border: '#f4c4c4' },
};

function fmtDate(iso: unknown): string {
    if (!iso) return '—';
    const isoStr = typeof iso === 'string' || iso instanceof Date ? String(iso) : '';
    if (!isoStr) return '—';
    try {
        return new Date(isoStr).toLocaleString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return isoStr.slice(0, 16); }
}

function fmtDuration(ms: unknown): string {
    const n = Number(ms);
    if (ms == null || Number.isNaN(n)) return '—';
    if (n === 0)   return '0ms';
    if (!Number.isFinite(n) || n < 0) return 'in progress';
    if (n < 1000)  return n + 'ms';
    if (n < 60000) return (n / 1000).toFixed(1) + 's';
    const m = Math.floor(n / 60000);
    const s = Math.round((n % 60000) / 1000);
    return s === 0 ? m + 'm' : m + 'm ' + s + 's';
}

function chipTpl(label: string, overrides?: Record<string, PkChipStyle>): TemplateResult {
    const key = (label ?? '').toLowerCase();
    // Consumer overrides (chipStyles config) win over the built-in palette.
    const override = overrides ? overrides[key] ?? overrides[label] : undefined;
    const c = override ?? CHIP_COLOURS[key] ?? { bg: '#f3f1ea', fg: '#3a4156', border: '#ddd' };
    const border = c.border ?? '#ddd';
    return html`<span
        class="chip"
        style="background:${c.bg};color:${c.fg};border:1px solid ${border};
               padding:1px 6px;border-radius:9999px;font-size:0.75rem;white-space:nowrap;"
    >${label}</span>`;
}

function cellTpl(value: unknown, type: string | undefined, chipOverrides?: Record<string, PkChipStyle>): TemplateResult {
    switch (type) {
        case 'date':
            return html`${fmtDate(value)}`;
        case 'duration':
            return html`${fmtDuration(value)}`;
        case 'bool-tick':
            return html`${value ? '✓' : '—'}`;
        case 'chips': {
            const fallback = value == null ? '' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
            const labels: string[] = Array.isArray(value)
                ? value.map((v) => typeof v === 'object' ? JSON.stringify(v) : String(v))
                : fallback.split(',').map(s => s.trim()).filter(Boolean);
            return html`${labels.map((label) => chipTpl(label, chipOverrides))}`;
        }
        default: {
            const out = value == null ? '—' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
            return html`${out}`;
        }
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<pk-data-table>` — config-driven data table with cursor pagination.
 *
 * Attributes:
 *   `src`           — URL to GET (returns `{ items, total?, nextCursor? }`).
 *   `filter-active` — boolean; when set, the empty-state copy switches to
 *                     `config.emptyFilterText` (if supplied). Use to
 *                     distinguish "no matches" from "no rows ever".
 *
 * Methods:
 *   `setConfig(config)` — programmatic configuration. The config object
 *                         accepts `emptyFilterText` and `chipStyles` in
 *                         addition to the columns / pageSize / src fields.
 *
 * Events:
 *   `pk-row-click` — bubbles; detail = `{ row }`.
 *
 * Slots:
 *   `empty-cta` — optional call-to-action rendered inside the empty state.
 */
export class PkDataTable extends HTMLElement {
    static readonly tagName = 'pk-data-table';

    static get observedAttributes(): string[] {
        return ['src', 'filter-active'];
    }

    private readonly _root: ShadowRoot;

    // State
    private _config: PkDataTableConfig | null = null;
    private _rows: unknown[]   = [];
    private _total: number     = 0;
    private _nextCursor: string | null = null;
    private _cursor: string | null     = null;
    private _page: number      = 0;
    private _loadState: LoadState = 'idle';
    private _errorMsg: string  = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        // Attribute-based configuration: if src is set on the element we build
        // a minimal config and trigger a fetch. Full column config needs setConfig().
        const attrSrc = this.getAttribute('src');
        if (attrSrc && !this._config) {
            this._config = { src: attrSrc, columns: [] };
        }
        if (this._config) {
            this._loadData();
        } else {
            this._doRender();
        }
    }

    attributeChangedCallback(name: string, _old: string | null, next: string | null): void {
        if (name === 'src' && next !== null) {
            if (this._config) {
                this._config = { ...this._config, src: next };
            } else {
                this._config = { src: next, columns: [] };
            }
            this._reset();
            if (this.isConnected) this._loadData();
            return;
        }
        if (name === 'filter-active') {
            // Re-render only — no refetch. The consumer is responsible for
            // updating `src` separately if they want the filter to affect
            // the fetch URL.
            if (this.isConnected) this._doRender();
        }
    }

    /** Fully configure and trigger first fetch. */
    setConfig(config: PkDataTableConfig): void {
        this._config = config;
        this._reset();
        if (this.isConnected) this._loadData();
    }

    /** Reload from first page. */
    refresh(): void {
        this._reset();
        if (this.isConnected) this._loadData();
    }

    private _reset(): void {
        this._rows       = [];
        this._total      = 0;
        this._nextCursor = null;
        this._cursor     = null;
        this._page       = 0;
        this._errorMsg   = '';
    }

    private async _loadData(): Promise<void> {
        if (!this._config) return;
        this._loadState = 'loading';
        this._doRender();

        const { src, pageSize = 50 } = this._config;
        const sep = src.includes('?') ? '&' : '?';
        const url = this._cursor
            ? `${src}${sep}take=${pageSize}&cursor=${encodeURIComponent(this._cursor)}`
            : `${src}${sep}take=${pageSize}`;

        try {
            const resp = await fetch(url, withCsrf());
            if (!resp.ok) {
                const text = await resp.text();
                throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200)}`);
            }
            const data  = await resp.json() as { items?: unknown[]; data?: unknown[]; total?: number; nextCursor?: string };
            const items = data.items ?? data.data ?? [];
            this._rows       = this._page === 0 ? items : [...this._rows, ...items];
            this._total      = data.total ?? items.length;
            this._nextCursor = data.nextCursor ?? null;
            this._loadState  = 'ok';
        } catch (err) {
            this._loadState = 'error';
            this._errorMsg  = err instanceof Error ? err.message : String(err);
        }
        this._doRender();
    }

    // ── Templates ──────────────────────────────────────────────────────────

    private _skeletonTpl(): TemplateResult {
        const cols = this._config?.columns.length ?? 4;
        const skRows = Array.from({ length: 5 });
        return html`
            <style>${styles}</style>
            <div class="table-wrap" aria-busy="true" aria-label="Loading data">
                <table role="table">
                    <thead>
                        <tr>
                            ${Array.from({ length: cols }, () => html`<th>&nbsp;</th>`)}
                        </tr>
                    </thead>
                    <tbody>
                        ${skRows.map(() => html`
                            <tr class="skeleton-row">
                                ${Array.from({ length: cols }, (_, i) => html`
                                    <td>
                                        <div class="skeleton-cell" style="width:${60 + (i * 13) % 30}%"></div>
                                    </td>
                                `)}
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
        `;
    }

    private _emptyTpl(): TemplateResult {
        const filterActive = this.hasAttribute('filter-active');
        const filterText   = this._config?.emptyFilterText ?? '';
        const message      = filterActive && filterText
            ? filterText
            : 'No rows yet.';
        return html`
            <style>${styles}</style>
            <div class="empty-state">
                <p>${message}</p>
                <slot name="empty-cta"></slot>
            </div>
        `;
    }

    private _errorTpl(): TemplateResult {
        return html`
            <style>${styles}</style>
            <div class="error-state" role="alert">
                <p>${this._errorMsg || 'Something went wrong.'}</p>
                <button class="retry-btn" type="button"
                    @click=${() => { this._reset(); this._loadData(); }}>
                    Retry
                </button>
            </div>
        `;
    }

    private _tableTpl(): TemplateResult {
        const cols  = this._config?.columns ?? [];
        const rows  = this._rows;
        const hasMore = this._nextCursor !== null && rows.length < this._total;

        return html`
            <style>${styles}</style>
            <div class="table-wrap">
                <table role="table" aria-rowcount=${this._total}>
                    <thead>
                        <tr role="row">
                            ${cols.map(c => html`<th role="columnheader">${c.label}</th>`)}
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map((row, idx) => html`
                            <tr role="row"
                                class="clickable"
                                tabindex="0"
                                aria-label="Row ${idx + 1}"
                                @click=${() => this._emitRowClick(row)}
                                @keydown=${(e: KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this._emitRowClick(row);
                                    }
                                }}>
                                ${cols.map(c => html`
                                    <td role="cell">${cellTpl(
                                        (row as Record<string, unknown>)[c.key],
                                        c.type,
                                        this._config?.chipStyles,
                                    )}</td>
                                `)}
                            </tr>
                        `)}
                    </tbody>
                </table>
                <div class="table-footer">
                    <span class="row-count">Showing ${rows.length}${this._total > 0 ? ` of ${this._total}` : ''}</span>
                    ${hasMore ? html`
                        <button class="load-more" type="button"
                            @click=${() => this._loadMore()}>
                            Load more
                        </button>
                    ` : nothing}
                </div>
            </div>
        `;
    }

    private template(): TemplateResult {
        switch (this._loadState) {
            case 'loading': return this._skeletonTpl();
            case 'error':   return this._errorTpl();
            case 'ok':
                return this._rows.length === 0 ? this._emptyTpl() : this._tableTpl();
            default:
                return html`<style>${styles}</style>`;
        }
    }

    private _doRender(): void {
        render(this.template(), this._root);
    }

    private _emitRowClick(row: unknown): void {
        this.dispatchEvent(new CustomEvent('pk-row-click', {
            bubbles: true,
            composed: true,
            detail: { row },
        }));
    }

    private _loadMore(): void {
        this._cursor = this._nextCursor;
        this._page++;
        this._loadData();
    }
}

if (!customElements.get(PkDataTable.tagName)) {
    customElements.define(PkDataTable.tagName, PkDataTable);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-data-table': PkDataTable;
    }
}
